import React, { useEffect, useRef, useState } from 'react';
import { useIdentityContext } from '../../components/IdentityProvider';
// Media state imports - optional for web app
// import { useMediaState } from '../../components/MediaStateProvider';
// import { useScreenShareSelectionStore } from '../../stores/screenShareSelectionStore';
import { useTheme } from '@flik/shared';
import { MicrophoneIcon, VideoIcon } from '../../components/icons';
import QuickActionMenu from '../../components/QuickActionMenu';
import { SEARCH_BAR_CONSTANTS, WINDOW_CONSTANTS } from '../../config.ts';
import { HeaderProps } from '../types';

// Modern magnify icon component
const MagnifyIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

// Back arrow icon component
const BackArrowIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5M12 19l-7-7 7-7" />
  </svg>
);

// Screen sharing icon component - matches exact styling of other icons
const ScreenShareIcon: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`transition-colors duration-200 ${
      isActive ? 'text-green-500' : 'text-gray-500'
    }`}
  >
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
    {!isActive && (
      <line
        x1="1"
        y1="1"
        x2="23"
        y2="23"
        stroke="currentColor"
        strokeWidth="2"
      />
    )}
  </svg>
);

// Subtle media status indicator component - disabled for web app
const MediaStatusIndicator: React.FC<{ className?: string }> = ({
  className = '',
}) => {
  // Media state functionality disabled for web app
  // const { isMicOn, isVideoOn } = useMediaState();
  // const { selectedSource } = useScreenShareSelectionStore();

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* Media status indicators disabled for web app */}
      {/* Microphone status indicator */}
      <div
        title="Microphone status unavailable"
        className="flex items-center justify-center"
      >
        <MicrophoneIcon isActive={false} />
      </div>

      {/* Video status indicator */}
      <div
        title="Camera status unavailable"
        className="flex items-center justify-center"
      >
        <VideoIcon isActive={false} />
      </div>

      {/* Screen sharing status indicator */}
      <div
        title="Screen sharing status unavailable"
        className="flex items-center justify-center"
      >
        <ScreenShareIcon isActive={false} />
      </div>
    </div>
  );
};

interface SearchHeaderProps extends HeaderProps {
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  actionButton?: React.ReactNode;
  shouldAutoFocus?: boolean;
  onAutoFocusComplete?: () => void;
  applet?: any;
}

const SearchHeader: React.FC<SearchHeaderProps> = ({
  searchQuery,
  onSearchChange,
  onKeyDown,
  onSignInClick,
  onDoubleClick,
  isActionMode = false,
  actionTitle,
  onBackClick,
  actionButton,
  requestWindowResize,
  isCollapsed = false,
  hasSession = false,
  onClearSearch,
  shouldAutoFocus = false,
  onAutoFocusComplete,
  applet,
}) => {
  const { isLoggedIn } = useIdentityContext();
  const { provideTailwind } = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState<string>(
    SEARCH_BAR_CONSTANTS.DEFAULT_WIDTH,
  );

  // Auto-focus effect with guard to prevent infinite loops
  const hasAutoFocused = useRef(false);

  useEffect(() => {
    if (
      shouldAutoFocus &&
      isLoggedIn &&
      !isActionMode &&
      !hasAutoFocused.current
    ) {
      hasAutoFocused.current = true; // Prevent multiple attempts

      // Request window focus first (async, don't wait)
      if (applet && applet.requestWindowFocus) {
        applet.requestWindowFocus();
      }

      // Focus the input immediately if ref is available, otherwise retry quickly
      let retryCount = 0;
      const maxRetries = 50; // Maximum 500ms of retries

      const focusInput = () => {
        if (inputRef.current) {
          inputRef.current.focus();
          if (onAutoFocusComplete) {
            onAutoFocusComplete();
          }
        } else if (retryCount < maxRetries) {
          retryCount++;
          // Retry after just 10ms if ref not ready
          setTimeout(focusInput, 10);
        }
      };

      // Start focusing immediately
      focusInput();
    } else if (!shouldAutoFocus) {
      // Reset the guard when shouldAutoFocus becomes false
      hasAutoFocused.current = false;
    }
  }, [shouldAutoFocus, isLoggedIn, isActionMode, applet, onAutoFocusComplete]);

  // Measure text width dynamically
  useEffect(() => {
    if (measureRef.current) {
      const textWidth = measureRef.current.offsetWidth;
      const calculatedWidth = Math.max(
        SEARCH_BAR_CONSTANTS.MIN_WIDTH,
        Math.min(
          SEARCH_BAR_CONSTANTS.MAX_WIDTH,
          textWidth + SEARCH_BAR_CONSTANTS.PADDING_OFFSET,
        ),
      ); // Add padding
      setInputWidth(`${calculatedWidth}px`);
    }
  }, [searchQuery]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle arrow keys for navigation
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
      // Don't prevent default here, let the SearchContent handle it
      // Just call the parent's onKeyDown handler
      if (onKeyDown) {
        onKeyDown(e);
      }
      return;
    }

    // Call the parent's onKeyDown handler for other keys
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const handleQuickActionMenuToggle = async (isOpen: boolean) => {
    if (!requestWindowResize || !WINDOW_CONSTANTS) return;

    try {
      // Only resize if we're in collapsed state (no search query, no badges, and no results)
      if (isCollapsed) {
        const newHeight = isOpen
          ? WINDOW_CONSTANTS.HEIGHT_COLLAPSED_WITH_QUICK_ACTIONS
          : WINDOW_CONSTANTS.HEIGHT_COLLAPSED;

        await requestWindowResize(WINDOW_CONSTANTS.WIDTH, newHeight);
      }
    } catch (error) {
      console.error('Failed to resize window for kebab menu:', error);
    }
  };

  return (
    <>
      {/* Hidden span to measure text width */}
      <span
        ref={measureRef}
        className="absolute invisible whitespace-pre text-base sm:text-sm"
        style={{
          fontFamily: 'inherit',
          fontSize: 'inherit',
          fontWeight: 'inherit',
        }}
      >
        {searchQuery || SEARCH_BAR_CONSTANTS.PLACEHOLDER_TEXT}
      </span>

      {/* Icon - Magnify in search mode, Back arrow in action mode */}
      <div
        className={`search-icon-wrapper mr-3 ${provideTailwind(
          'text-primary',
        )} flex items-center`}
        style={{
          zIndex: 20,
          pointerEvents: 'auto',
          minWidth: '32px',
          minHeight: '32px',
        }}
      >
        {isActionMode ? (
          <button
            onClick={() => {
              if (onBackClick) {
                onBackClick();
              }
            }}
            className="p-2 rounded-lg transition-colors window-no-drag cursor-pointer"
            title="Back to search"
            style={{
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 25,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BackArrowIcon />
          </button>
        ) : (
          <div className="flex items-center justify-center w-8 h-8">
            <MagnifyIcon />
          </div>
        )}
      </div>

      {/* Content area */}
      {isActionMode ? (
        // Action mode - show title
        <div className="flex-1 flex items-center" style={{ zIndex: 10 }}>
          <h2
            className={`${provideTailwind(
              'text-primary',
            )} text-base sm:text-sm font-medium leading-none`}
          >
            {actionTitle}
          </h2>
        </div>
      ) : (
        // Search mode - show input
        <>
          {isLoggedIn ? (
            <input
              ref={inputRef}
              type="text"
              className={`bg-transparent border-none outline-none ${provideTailwind(
                'text-primary',
              )} text-base sm:text-sm placeholder:text-gray-300 z-10 relative flex-1`}
              style={{
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              placeholder={SEARCH_BAR_CONSTANTS.PLACEHOLDER_TEXT}
              value={searchQuery}
              onChange={onSearchChange}
              onKeyDown={handleKeyDown}
              onDoubleClick={onDoubleClick}
            />
          ) : (
            <button
              onClick={onSignInClick}
              className={`bg-transparent border-none outline-none ${provideTailwind(
                'text-primary',
              )} text-base sm:text-sm placeholder:text-gray-300 z-10 relative cursor-pointer hover:text-gray-200 transition-colors flex-1`}
              style={{
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Sign in to Flik
            </button>
          )}
        </>
      )}

      {/* Right side buttons */}
      {!isActionMode && (
        <div className="pr-0 flex items-center space-x-2">
          {/* Media status indicator - only show when logged in and session has started */}
          {isLoggedIn && hasSession && (
            <MediaStatusIndicator className="window-no-drag" />
          )}

          {/* Quick action menu - only show when not in action mode */}
          <QuickActionMenu
            onMenuToggle={handleQuickActionMenuToggle}
            onClearSearch={onClearSearch}
            requestWindowResize={requestWindowResize}
          />
        </div>
      )}

      {isActionMode && (
        <div className="pr-3">
          {/* Action mode - show action button if provided */}
          {actionButton}
        </div>
      )}
    </>
  );
};

export { SearchHeader };
